/// <amd-module name="Wit/WorkItemForm/Attachments/AttachmentsTab" />
import * as React from "react";
import { IObservableArray } from "VSS/Core/Observable";
import { Button } from "VSSUI/Button";
import { copyToClipboard } from "VSSUI/Clipboard";
import { Link } from "VSSUI/Link";
import { Observer } from "VSSUI/Observer";
import { IZeroDataProps, ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import { ILinkInfo } from "Wit/WorkItemDataManager/Contracts";
import * as Resources from "Wit/WorkItemForm/Resources";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { CollapsibleSection, EditCommentDialog } from "../Common/Components";
import { IWorkItemFormPageContext, WorkItemFormPageContext } from "../Common/Hooks";
import { IWorkItemFormAttachmentsService } from "../Common/Services";
import { IAttachmentActions } from "./AttachmentActions";
import { AttachmentsGrid } from "./AttachmentsGrid";
import { AttachmentsViewToggle } from "./AttachmentsViewToggle";
import { AttachmentThumbnails } from "./AttachmentThumbnails";
import { downloadAttachment } from "./AttachmentUtils";
import { AttachmentPreview } from "./Dialogs/AttachmentPreview";
import { DeleteAttachmentDialog } from "./Dialogs/DeleteAttachmentDialog";
import { getAttachmentUri } from "./useAttachmentUri";

export interface IAttachmentsTabProps {}

export function AttachmentsTab(props: IAttachmentsTabProps) {
    return (
        <div className="work-item-form-tab full-width">
            <CollapsibleSection label={Resources.Attachments}>
                <AttachmentsSection {...props} />
            </CollapsibleSection>
        </div>
    );
}

interface IAttachmentsSectionState {
    viewType: "grid" | "thumbnail";
    previewingIndex: number;
    editingIndex: number;
    deletingIndex: number;
}

export class AttachmentsSection extends React.Component<IAttachmentsTabProps, IAttachmentsSectionState> {
    static contextType = WorkItemFormPageContext;
    // For typing the context in TS
    public context: IWorkItemFormPageContext;

    private _workItem: IWorkItem;
    private _attachments: IObservableArray<ILinkInfo>;
    private _attachmentsService: IWorkItemFormAttachmentsService;
    private _fileInputRef: React.RefObject<HTMLInputElement>;
    private _isWorkItemReadOnly: boolean;

    constructor(props: IAttachmentsTabProps, context: IWorkItemFormPageContext) {
        super(props);

        if (!context.pageContext || context.workItem === null) {
            throw new Error("WorkItemFormPageContext can only be used inside a WorkItemFormPageContextProvider");
        }

        this.context = context;
        this._workItem = context.workItem;
        this._isWorkItemReadOnly = this._workItem.isReadOnly();
        this._attachmentsService = context.pageContext.getService<IWorkItemFormAttachmentsService>("IWorkItemFormAttachmentsService");
        this._fileInputRef = React.createRef();

        this.state = {
            viewType: "grid",
            previewingIndex: -1,
            editingIndex: -1,
            deletingIndex: -1
        };

        this._attachments = this._attachmentsService.getAttachmentsObservable(this._workItem);
    }

    /**
     * Opens the browser's native file picker
     */
    private onAddAttachment() {
        this._fileInputRef.current?.click();
    }

    private onCommitComment(attachment: ILinkInfo, newComment: string) {
        this._attachmentsService.editComment(this._workItem, attachment, newComment);
        this.setState({ editingIndex: -1 });
    }

    private onPreviewAttachment(attachment: ILinkInfo) {
        this.setState({ previewingIndex: this._attachments.value.findIndex(a => a.FilePath === attachment.FilePath) });
    }

    private previewNextAttachment() {
        if (this.state.previewingIndex < 0) {
            return;
        }
        const nextIndex = (this.state.previewingIndex + 1) % this._attachments.value.length;
        this.setState({ previewingIndex: nextIndex });
    }

    private previewPreviousAttachment() {
        if (this.state.previewingIndex < 0) {
            return;
        }
        const previousIndex = this.state.previewingIndex > 0 ? this.state.previewingIndex - 1 : this._attachments.value.length - 1;
        this.setState({ previewingIndex: previousIndex });
    }

    private onEditComment(attachment: ILinkInfo) {
        this.setState({ editingIndex: this._attachments.value.findIndex(a => a.FilePath === attachment.FilePath) });
    }

    private onDownloadAttachment(attachment: ILinkInfo) {
        if (!this.context.pageContext || !this.context.workItem) {
            return;
        }
        downloadAttachment(this.context.pageContext, this.context.workItem, attachment);
    }

    /**
     * Open the "Delete attachment" dialog
     */
    private onDeleteAttachment(attachment: ILinkInfo) {
        this.setState({ deletingIndex: this._attachments.value.findIndex(a => a.FilePath === attachment.FilePath) });
    }

    /**
     * Complete deletion of the attachment
     */
    private onConfirmDelete(attachment: ILinkInfo) {
        this._attachmentsService.deleteAttachment(this._workItem, attachment);
        this.setState({ deletingIndex: -1, previewingIndex: -1 });
    }

    private onCopyAttachmentUrl(attachment: ILinkInfo) {
        if (!this.context?.pageContext || !this.context?.workItem) {
            return "";
        }

        getAttachmentUri(this.context.pageContext, this.context.workItem.project.guid, attachment).then(uri => {
            const fileName = attachment.OriginalName ?? attachment.FilePath ?? "";
            uri += `?fileName=${fileName}&download=true`;
            const link = (
                <Link href={uri} download={fileName}>
                    {fileName}
                </Link>
            );
            copyToClipboard(link);
        });
    }

    public render() {
        return (
            <>
                {/* Hidden file input to handle uploading */}
                <input
                    ref={this._fileInputRef}
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    onChange={event => {
                        const fileList = event.target.files;
                        if (fileList && fileList.length) {
                            this._attachmentsService.attachFiles(this._workItem, Array.from(fileList));
                        }
                    }}
                />
                <Observer attachments={this._attachments}>
                    {({ attachments }: { attachments: ILinkInfo[] }) => (
                        <>
                            {attachments.length === 0 ? this.renderZeroData() : this.renderMainContent()}
                            {this.renderDialogs(attachments)}
                        </>
                    )}
                </Observer>
            </>
        );
    }

    private renderZeroData() {
        const zeroDataProps: IZeroDataProps = {
            iconProps: { iconName: "Attach" },
            imageAltText: Resources.AttachmentsZeroData,
            secondaryText: Resources.AttachmentsZeroData
        };

        if (!this._isWorkItemReadOnly) {
            zeroDataProps.actionText = Resources.AddAttachment;
            zeroDataProps.actionType = ZeroDataActionType.ctaButton;
            zeroDataProps.onActionClick = () => this.onAddAttachment();
        }
        return <ZeroData {...zeroDataProps} />;
    }

    private renderMainContent() {
        const actions: IAttachmentActions = {
            onPreviewAttachment: (a: ILinkInfo) => this.onPreviewAttachment(a),
            onDownloadAttachment: (a: ILinkInfo) => this.onDownloadAttachment(a),
            onCopyAttachmentUrl: (a: ILinkInfo) => this.onCopyAttachmentUrl(a)
        };

        if (!this._isWorkItemReadOnly) {
            actions.onDeleteAttachment = (a: ILinkInfo) => this.onDeleteAttachment(a);
            actions.onEditComment = (a: ILinkInfo) => this.onEditComment(a);
        }
        return (
            <>
                {/* Header */}
                <div className="flex-row justify-space-between">
                    <Button
                        className="add-attachment-button"
                        subtle
                        disabled={this._isWorkItemReadOnly}
                        text={Resources.AddAttachment}
                        iconProps={{ iconName: "Add" }}
                        onClick={() => this.onAddAttachment()}
                    />
                    <AttachmentsViewToggle
                        className="flex-self-end"
                        viewType={this.state.viewType}
                        onViewTypeChanged={viewType => this.setState({ viewType })}
                    />
                </div>
                {/* Main view - either a grid or thumbnails */}
                {this.state.viewType === "grid" ? (
                    <AttachmentsGrid
                        attachments={this._attachments}
                        {...actions}
                    />
                ) : (
                    <AttachmentThumbnails
                        attachments={this._attachments}
                        {...actions}
                    />
                )}
            </>
        );
    }

    private renderDialogs(attachments: ILinkInfo[]) {
        return (
            <>
                {this.state.previewingIndex > -1 && (
                    <AttachmentPreview
                        attachment={attachments[this.state.previewingIndex]}
                        onEditComment={!this._isWorkItemReadOnly ? (a) => this.onEditComment(a) : undefined}
                        onDeleteAttachment={!this._isWorkItemReadOnly ? (a) => this.onDeleteAttachment(a) : undefined}
                        onDownloadAttachment={a => this.onDownloadAttachment(a)}
                        onDismiss={() => this.setState({ previewingIndex: -1 })}
                        onPrevious={() => this.previewPreviousAttachment()}
                        onNext={() => this.previewNextAttachment()}
                    />
                )}

                {this.state.editingIndex > -1 && (
                    <EditCommentDialog
                        comment={attachments[this.state.editingIndex].Comment ?? ""}
                        onCommitComment={newComment => this.onCommitComment(attachments[this.state.editingIndex], newComment)}
                        onDismiss={() => this.setState({ editingIndex: -1 })}
                    />
                )}

                {this.state.deletingIndex > -1 && (
                    <DeleteAttachmentDialog
                        attachmentName={attachments[this.state.deletingIndex].OriginalName ?? ""}
                        onConfirmDelete={() => this.onConfirmDelete(attachments[this.state.deletingIndex])}
                        onDismiss={() => this.setState({ deletingIndex: -1 })}
                    />
                )}
            </>
        );
    }
}
